/**
 * templateSyncMeta — iter87.7. Single source of truth mapping every
 * tournament template to its Live Sync metadata: which upstream API
 * powers the feed, which fields are read-only from that API vs. still
 * editable by the admin, and how sync behaves.
 *
 * The Live Sync tile reads this to render a consistent "what's
 * powering scores here?" card for every template.
 */

const API_FOOTBALL = {
    api: "API-Football",
    provider: "RapidAPI · api-sports.io",
    upstream_url: "https://www.api-football.com/",
    docs_url: "https://www.api-football.com/documentation-v3",
    refresh_cadence: "Every ~15 minutes via cached upstream reads",
    actionable: false,
    manual: false,
};

const SLASH_GOLF = {
    api: "Slash Golf",
    provider: "RapidAPI · slashgolf.dev",
    upstream_url: "https://slashgolf.dev/",
    docs_url: null,
    refresh_cadence: "Round-hour cached upstream reads; admin can refresh on demand",
    actionable: true,
    manual: false,
};

// iter87.107 → iter87.368 — CFB templates. `cfb_playoffs_api` is the
// only one left (CFB Weekly Spread Boost / `cfb_boost_api` removed
// entirely). Primary live sync = API-Sports American Football (NCAA,
// league=2) — same vendor as NFL, different `league` param. That feed
// powers schedule, scores, live status, and standings.
const API_AMERICAN_FOOTBALL = {
    api: "API-Sports · American Football (NCAA)",
    provider: "RapidAPI · api-sports.io",
    upstream_url: "https://www.api-football.com/",
    docs_url: "https://www.api-sports.io/documentation/american-football/v1",
    refresh_cadence: "Every ~15 minutes via cached upstream reads",
    actionable: false,
    manual: false,
};

// iter87.227 — Highlightly (RapidAPI "Sport Highlights API" listing)
// powers NBA Playoffs (nba_playoffs_api) — see vendor_registry.py
// "Highlightly.io (RapidAPI)". Was previously missing from this map
// entirely for MLB (fell through to the MANUAL fallback) and wrongly
// mapped to API_FOOTBALL for NBA (a soccer vendor) — user-reported bug.
const HIGHLIGHTLY = {
    api: "Highlightly",
    provider: "RapidAPI · Highlightly.io",
    upstream_url: "https://rapidapi.com/highlightly-api-highlightly-api-default/api/sport-highlights-api",
    docs_url: "https://rapidapi.com/highlightly-api-highlightly-api-default/api/sport-highlights-api",
    refresh_cadence: "Every ~15 minutes via cached upstream reads; admin can run a manual sync",
    actionable: true,
    manual: false,
};

// Shared "secondary source" shape — rendered as a fallback callout by
// LiveSyncTab (`meta.secondary`) under the primary vendor card.
const HIGHLIGHTLY_FALLBACK = {
    api: "Highlightly",
    provider: "RapidAPI · Highlightly.io",
    scope: "Same-cycle fallback only — used for that one poll if the primary feed errors, then auto-switches back",
    refresh_cadence: "Not polled on its own schedule; only invoked on a primary-vendor failure",
    docs_url: "https://rapidapi.com/highlightly-api-highlightly-api-default/api/sport-highlights-api",
};

// iter87.373 (user-reported — "it shows Highlightly") — MLB Playoffs'
// live games/scores moved to the official, free MLB Stats API as
// PRIMARY back in iter87.328 (`mlb_engine.py`), with Highlightly kept
// only as a same-cycle fallback — this sync card never caught up to
// that migration and kept showing pure Highlightly.
const MLB_STATS_API = {
    api: "MLB Stats API",
    provider: "Official MLB · statsapi.mlb.com",
    upstream_url: "https://statsapi.mlb.com/",
    docs_url: "https://statsapi.mlb.com/docs/",
    refresh_cadence: "Every ~15 minutes via cached upstream reads",
    actionable: false,
    manual: false,
    secondary: HIGHLIGHTLY_FALLBACK,
};

const MLB_PLAYOFFS_FIELDS = [
    { field: "Bracket + round drawings", from: "api" },
    { field: "Game schedule & start times", from: "api" },
    { field: "Final scores + series winner", from: "api" },
    { field: "Standings-derived seeds 1-6 (AL/NL)", from: "api" },
    { field: "Team Pick Pot tranches & multipliers", from: "manual", note: "Curated per season" },
    { field: "Champions Pick + Series Predictor bonus", from: "system", note: "Fixed engine value" },
];

// iter87.361 — NWSL Playoffs. Pure pick'em (no Team Pick Pot row,
// unlike MLB/NBA above) — fixed 8-team bracket, no reseeding.
const NWSL_PLAYOFFS_FIELDS = [
    { field: "Fixed 8-team bracket (QF/SF/Final)", from: "api" },
    { field: "Game schedule & start times", from: "api" },
    { field: "Final scores + resolution (Reg/ET/PK)", from: "api" },
    { field: "Standings-derived seeds 1-8", from: "api" },
    { field: "Champions Pick + Dark Horse Pick bonus", from: "system", note: "Admin-editable points" },
    { field: "Playoff Predictor (Winner/Decided-by/Goals)", from: "system", note: "Fixed engine value, +2.5 goals line" },
];

const NBA_PLAYOFFS_FIELDS = [
    { field: "Bracket + round drawings", from: "api" },
    { field: "Game schedule & start times", from: "api" },
    { field: "Final scores + series winner", from: "api" },
    { field: "Standings-derived seeds 1-10 (East/West)", from: "api" },
    { field: "Team Pick Pot tranches & multipliers", from: "manual", note: "Curated per season" },
    { field: "Champions Pick + Series Predictor bonus", from: "system", note: "Fixed engine value" },
];

// iter87.465.20 — MLS Playoffs. Pure pick'em like NWSL above, plus a
// Wild Card Play-In Bonus (pre-season primitive) and a Round One
// Series Predictor (MLB/NBA-shaped, in-season); Conf Semis+ reuse the
// generic Match Predictor (Asian handicap/O-U) infra, iter87.465.24
// added real odds via The Odds API (`soccer_usa_mls`).
const MLS_PLAYOFFS_FIELDS = [
    { field: "18-team bracket (East/West seeding)", from: "api" },
    { field: "Game schedule & start times", from: "api" },
    { field: "Final scores + series/game resolution", from: "api" },
    { field: "Standings-derived seeds 1-9 per conference", from: "api" },
    { field: "Champions Pick + Dark Horse Pick + Play-In Bonus", from: "system", note: "Admin-editable points" },
    { field: "Series Predictor (Round One) + Playoff Predictor (Conf Semis+)", from: "system", note: "Fixed engine values" },
];

// iter87.466.1 — UCL Playoffs. Pure pick'em like NWSL/MLS above,
// 2-leg aggregate ties (Knockout Round Play-offs through Semis) + a
// single-game Final. API-Football, league=2.
const UCL_PLAYOFFS_FIELDS = [
    { field: "24-team field (seeds 1-24, byes for top 8)", from: "api" },
    { field: "2-leg ties + Final schedule & start times", from: "api" },
    { field: "Leg scores + aggregate/advance winner", from: "api" },
    { field: "League Phase standings-derived seeds", from: "api" },
    { field: "UCL Champion Pick + Dark Horse Pick", from: "system", note: "Admin-editable points" },
    { field: "Two-Leg Predictor + Champions Predictor (Final)", from: "system", note: "Fixed engine values" },
];

// iter87.371 → iter87.374 — CFB Playoffs field map. Round win points +
// the Playoff Predictor are fixed engine rules; no Champions Pick /
// Series Predictor (MLB/NBA-only mechanics). Bracket/game results come
// from API-Sports PRIMARILY, Highlightly kept only as a same-cycle
// fallback. NFL Playoffs retired the Team Pick Pot draft in iter87.374
// (see `NFL_PO_FIELDS` below) — CFB is unaffected.
const CFB_PO_FIELDS = [
    { field: "Bracket + round drawings", from: "api" },
    { field: "Game schedule & kick-off times", from: "api" },
    { field: "Final scores + round winner", from: "api" },
    { field: "Admin-curated playoff seeds", from: "api" },
    { field: "Team Pick Pot tranches & power-pick multipliers", from: "manual", note: "Curated per season" },
    { field: "Playoff Predictor (Spread + Over/Under, 2/round, 1 for the Championship)", from: "system", note: "Fixed engine rule" },
];

// iter87.374 (user-requested — "follow the NWSL format ... remove
// Power picks") — NFL Playoffs field map. No draft/Team Pick Pot
// anymore — Super Bowl Champion + Dark Horse Picks are fixed engine
// rules (pure pick'em, same shape as NWSL). The Playoff Predictor
// sidecar stays unchanged.
const NFL_PO_FIELDS = [
    { field: "Bracket + round drawings", from: "api" },
    { field: "Game schedule & kick-off times", from: "api" },
    { field: "Final scores + round winner", from: "api" },
    { field: "Live conference standings & playoff seeds", from: "api" },
    { field: "Super Bowl Champion (1-2 picks, half-points if hedging)", from: "system", note: "Fixed engine rule" },
    { field: "Dark Horse Picks (AFC + NFC, seed 5-7 only, +pts/win)", from: "system", note: "Fixed engine rule" },
    { field: "Playoff Predictor (Spread + Over/Under, 2/round, 1 for the Super Bowl)", from: "system", note: "Fixed engine rule" },
];

const MANUAL = {
    api: null,
    provider: "Manual",
    upstream_url: null,
    docs_url: null,
    refresh_cadence: "None — the admin enters results by hand",
    actionable: false,
    manual: true,
};

const CSV_FEED = {
    api: "GotSport CSV / uploaded feed",
    provider: "CSV upload — GotSport, Sportlots, custom",
    upstream_url: null,
    docs_url: null,
    refresh_cadence: "Each CSV upload replaces the current feed snapshot",
    actionable: true,
    manual: false,
};

// Field-map descriptors: "field → read-only?"
const CLUB_LEAGUE_FIELDS = [
    { field: "Fixture list & kick-off times", from: "api" },
    { field: "Final scores & goals", from: "api" },
    { field: "Live status (LIVE / FT / HT)", from: "api" },
    { field: "League table & form", from: "api" },
    { field: "Draft pots & tiering", from: "manual", note: "Curated per season" },
    { field: "Champion Bonus decay + points", from: "manual" },
];

const NFL_FIELDS = [
    { field: "Fixture list & kick-off times", from: "api" },
    { field: "Final scores + spread cover", from: "api" },
    { field: "Live status (LIVE / FT / HT)", from: "api" },
    { field: "Standings & records", from: "api" },
    { field: "Team pots (Contenders → Longshots)", from: "manual", note: "Curated pre-season" },
];

// iter87.111 — NFL weekly spread boost field map. Full slate every
// week (no pool filter), fixed 10 picks per week. Single vendor
// (API-Sports league=1); no secondary lookup.
const NFL_BOOST_FIELDS = [
    { field: "Weekly schedule + kick-off times", from: "api" },
    { field: "Live status (LIVE / FT / HT)", from: "api" },
    { field: "Final scores + winners", from: "api" },
    { field: "Spread line (frozen at kickoff)", from: "api", note: "Locked from API-Sports at first kickoff each week" },
    { field: "Boost lock windows (kickoff)", from: "api", note: "Locks on first kickoff each week" },
    { field: "Picks per round", from: "system", note: "Fixed 10 per week" },
];

const KO_FIELDS = [
    { field: "Bracket + round drawings", from: "api" },
    { field: "Fixture list & kick-off times", from: "api" },
    { field: "Final scores + advancing team", from: "api" },
    { field: "Group stage results (if applicable)", from: "api" },
    { field: "Team seeds & pots", from: "manual", note: "Curated per event" },
    { field: "Champion Bonus decay + points", from: "manual" },
];

const GOLF_FIELDS = [
    { field: "Event schedule + purse", from: "api" },
    { field: "Field / entrants + tee times", from: "api" },
    { field: "Live scores per round", from: "api" },
    { field: "Cut line + WD/DQ status", from: "api" },
    { field: "Final leaderboard", from: "api" },
    { field: "Golfer pots (pickem_lowest_avg only)", from: "manual", note: "Curated per event" },
];

const MANUAL_FIELDS = [
    { field: "Fixture list & kick-off times", from: "manual" },
    { field: "Final scores", from: "manual" },
    { field: "Advancement / round marking", from: "manual" },
    { field: "Team seeds & pots", from: "manual" },
];

const CSV_FIELDS = [
    { field: "Fixture list & kick-off times", from: "csv" },
    { field: "Final scores", from: "csv" },
    { field: "Team roster / roster updates", from: "csv" },
    { field: "Advancement / round marking", from: "manual", note: "Admin marks rounds complete" },
    { field: "Draft pots", from: "manual" },
];

// iter87.180.12 — GotSport is its own data source. Same manual bits as
// CSV (advancement + pot draws) but the automatic fields come "FROM
// GOTSPORT" so the read-only field map doesn't lie to the admin.
const GOTSPORT_FIELDS = [
    { field: "Fixture list & kick-off times", from: "gotsport" },
    { field: "Final scores", from: "gotsport" },
    { field: "Team roster / roster updates", from: "gotsport" },
    { field: "Advancement / round marking", from: "manual", note: "Admin marks rounds complete" },
    { field: "Draft pots", from: "manual" },
];

/**
 * Return the sync metadata for a tournament based on its `template`
 * key + a few discriminators the wizard sets on the tournament doc.
 */
export function getTemplateSyncMeta(tournament) {
    if (!tournament) return { ...MANUAL, fields: MANUAL_FIELDS, template_label: "Unknown" };
    const t = tournament.template || "";

    // Club-league season templates → API-Football
    if (t === "premier_league_api") {
        return { ...API_FOOTBALL, fields: CLUB_LEAGUE_FIELDS, template_label: "Premier League" };
    }
    if (t === "la_liga_api") {
        return { ...API_FOOTBALL, fields: CLUB_LEAGUE_FIELDS, template_label: "La Liga" };
    }
    if (t === "serie_a_api") {
        return { ...API_FOOTBALL, fields: CLUB_LEAGUE_FIELDS, template_label: "Serie A" };
    }
    if (t === "club_season_api") {
        return { ...API_FOOTBALL, fields: CLUB_LEAGUE_FIELDS, template_label: "Club Season" };
    }
    // iter87.199.188 — Champions League was missing from this map
    // entirely, so it fell through to the "Custom manual-only
    // fallback" at the bottom and showed "LIVE SYNC — MANUAL" despite
    // being a fully API-Football-backed template, same vendor + cadence
    // as Premier League / La Liga / Serie A.
    if (t === "champions_league_api") {
        return { ...API_FOOTBALL, fields: KO_FIELDS, template_label: "Champions League" };
    }

    // NFL
    if (t === "nfl_season_api") {
        return { ...API_FOOTBALL, fields: NFL_FIELDS, template_label: "NFL Regular Season" };
    }
    // iter87.111 — NFL Weekly Spread Boost. Same API-Sports league=1
    // feed as NFL Season (single vendor, no secondary source). Field
    // map mirrors CFB Boost — both run on API-Sports alone.
    if (t === "nfl_boost_api") {
        return {
            ...API_AMERICAN_FOOTBALL,
            api: "API-Sports · American Football (NFL)",
            docs_url: "https://www.api-sports.io/documentation/american-football/v1",
            fields: NFL_BOOST_FIELDS,
            template_label: "NFL Weekly Pick'em",
        };
    }
    if (t === "nfl_playoffs_api") {
        return {
            ...API_AMERICAN_FOOTBALL,
            api: "API-Sports · American Football (NFL)",
            fields: NFL_PO_FIELDS,
            template_label: "NFL Playoffs",
            secondary: HIGHLIGHTLY_FALLBACK,
        };
    }

    // World Cup / Euros
    if (t === "world_cup_api") {
        return { ...API_FOOTBALL, fields: KO_FIELDS, template_label: "World Cup (group + KO)" };
    }
    if (t === "world_cup_ko_api" || t === "euros_ko_api") {
        return { ...API_FOOTBALL, fields: KO_FIELDS, template_label: "KO-only Bracket" };
    }

    // College basketball / NCAA
    if (t === "march_madness_api" || t === "ncaa_mens_api") {
        return { ...API_FOOTBALL, fields: KO_FIELDS, template_label: "March Madness (NCAA)" };
    }

    // iter87.213 — MLB Playoffs. Was missing from this map entirely,
    // so it fell through all the way to the "Custom manual-only
    // fallback" and showed "LIVE SYNC — MANUAL" despite being fully
    // API-backed (bracket, scores, standings auto-sync). iter87.373 —
    // official MLB Stats API is now primary (iter87.328), Highlightly
    // kept only as a same-cycle fallback.
    if (t === "mlb_playoffs_api") {
        return { ...MLB_STATS_API, fields: MLB_PLAYOFFS_FIELDS, template_label: "MLB Playoffs" };
    }

    // NBA — iter87.225. Was wrongly mapped to API_FOOTBALL (a soccer
    // vendor); NBA Playoffs shares MLB's Highlightly/RapidAPI vendor.
    if (t === "nba_playoffs_api" || t === "nba_finals_api") {
        return { ...HIGHLIGHTLY, fields: NBA_PLAYOFFS_FIELDS, template_label: "NBA Playoffs" };
    }

    // iter87.230 → iter87.373 — CFB Playoffs, sibling of NFL Playoffs
    // above (API-Sports NCAA feed, same Highlightly fallback).
    if (t === "cfb_playoffs_api") {
        return {
            ...API_AMERICAN_FOOTBALL,
            api: "API-Sports · American Football (NCAA)",
            fields: CFB_PO_FIELDS,
            template_label: "CFB Playoffs",
            secondary: HIGHLIGHTLY_FALLBACK,
        };
    }

    // iter87.361 — NWSL Playoffs. API-Football vendor (league=254),
    // NOT Highlightly — pure pick'em, no Team Pick Pot.
    if (t === "nwsl_playoffs_api") {
        return { ...API_FOOTBALL, fields: NWSL_PLAYOFFS_FIELDS, template_label: "NWSL Playoffs" };
    }

    // iter87.465.20 — MLS Playoffs, sibling of NWSL above (API-Football, league=253).
    if (t === "mls_playoffs_api") {
        return { ...API_FOOTBALL, fields: MLS_PLAYOFFS_FIELDS, template_label: "MLS Playoffs" };
    }

    // iter87.466.1 — UCL Playoffs, sibling of MLS above (API-Football, league=2).
    if (t === "ucl_playoffs_api") {
        return { ...API_FOOTBALL, fields: UCL_PLAYOFFS_FIELDS, template_label: "UCL Playoffs" };
    }

    // College Football Playoff bracket — API-Sports NCAA feed.
    if (t === "football_playoffs_api" || t === "cfp_api") {
        return {
            ...API_AMERICAN_FOOTBALL,
            fields: KO_FIELDS,
            template_label: "College Football Playoff",
        };
    }

    // Olympics
    if (t === "olympics_api") {
        return { ...API_FOOTBALL, fields: KO_FIELDS, template_label: "Olympics medal tournament" };
    }

    // Golf
    if (t === "golf_pickem_lowest_avg_api" || tournament?.format === "golf") {
        return { ...SLASH_GOLF, fields: GOLF_FIELDS, template_label: "Golf event" };
    }

    // Custom w/ feed (CSV / GotSport / API-Football)
    //
    // iter87.143 — Also read the wizard-picked `tournament.data_source`
    // as the source-of-truth for custom tournaments; the boolean flags
    // below live on the LEAGUE for legacy custom rows only, so a custom
    // tournament with `data_source="gotsport"` was previously falling
    // through to MANUAL here (which then made LiveSyncTab short-circuit
    // to `mode="manual"` before its own custom-data_source check could
    // fire).
    const ds = tournament?.data_source;
    const feedCsv = !!tournament?.feed_csv_enabled || ds === "csv";
    const gotsport = !!tournament?.gotsport_enabled || ds === "gotsport";
    if (gotsport && !feedCsv) {
        // iter87.180.12 — GotSport-only custom tournament. Distinct
        // header + field map so the UI doesn't misrepresent it as CSV.
        return {
            ...CSV_FEED,
            fields: GOTSPORT_FIELDS,
            template_label: "Custom · GotSport feed",
        };
    }
    if (feedCsv || gotsport) {
        return {
            ...CSV_FEED,
            fields: feedCsv ? CSV_FIELDS : GOTSPORT_FIELDS,
            template_label: gotsport && feedCsv
                ? "Custom · GotSport + CSV"
                : (feedCsv ? "Custom · CSV feed" : "Custom · GotSport feed"),
        };
    }
    // Custom with API-Football data_source picks up the API-Football
    // meta shape so the header reads "API-Football" not MANUAL.
    if (ds === "api_football") {
        return { ...API_FOOTBALL, fields: KO_FIELDS, template_label: "Custom · API-Football" };
    }

    // Custom manual-only fallback
    return { ...MANUAL, fields: MANUAL_FIELDS, template_label: "Custom · manual entry" };
}

export function fmtLastSync(iso) {
    if (!iso) return "never";
    try {
        const d = new Date(iso);
        const diff = (Date.now() - d.getTime()) / 1000;
        if (diff < 60) return "moments ago";
        if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
        if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
        return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
    } catch {
        return "unknown";
    }
}
